import {
  LazyQueryResultTuple,
  NetworkStatus,
  OperationVariables,
  QueryResult,
} from '@apollo/client';
import {
  faRefresh,
  faFilter,
  faColumns,
} from '@fortawesome/free-solid-svg-icons';
import { CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { Container, Box, Alert } from '@mui/material';
import { Row } from '@tanstack/react-table';
import {
  bind,
  concat,
  filter,
  includes,
  join,
  map,
  reduce,
  split,
  omit,
  isNil,
} from 'lodash';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { NoResultsText, NoResultsOverrideType } from './no-results-text';
import { Pagination } from '../../components/pagination/pagination';
import { Button, type ButtonProps } from '../button/button';
import { DropdownMultiSelect } from '../dropdown-multi-select/dropdown-multi-select';
import { Table } from '../table/table';
import { ColumnProps } from '../table/table-types';

// Breakpoint order for comparison
const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const;

// Helper function to get visible columns based on current breakpoint
const getVisibleColumnsByBreakpoint = <
  TData,
  TQuery,
  TVars extends OperationVariables,
>(
  columns: ColumnProps<TData, TQuery, TVars>[],
  currentBreakpoint: string,
): string[] => {
  const currentBreakpointIndex = BREAKPOINT_ORDER.indexOf(
    currentBreakpoint as any,
  );

  return columns
    .filter((column) => {
      // Always include columns without labels (button columns)
      if (!column.label) return false; // Don't include in the result array since we only want labeled columns

      // If no minimumBreakpoint is set, include the column
      if (!column.minimumBreakpoint) return true;

      // Include columns where current breakpoint is >= minimum required breakpoint
      const minBreakpointIndex = BREAKPOINT_ORDER.indexOf(
        column.minimumBreakpoint,
      );
      return currentBreakpointIndex >= minBreakpointIndex;
    })
    .map((column) => column.label!)
    .filter(Boolean); // Extra safety to remove any falsy values
};

export interface PaginationFilter {
  label: string;
  value: string;
  isActive?: boolean;
}

export interface PaginationResponse<D> {
  count?: number | null;
  pageInfo: {
    currentPage: number;
    perPage: number;
    pageCount?: number | null;
    itemCount?: number | null;
    hasNextPage?: boolean | null;
    hasPreviousPage?: boolean | null;
  };
  items?: D[] | null;
}

interface Option {
  label: string;
  value: string;
  isActive?: boolean;
}

export interface PaginationTableProps<
  TData,
  TAccessor extends PaginationResponse<TData>,
  TQuery,
  TVars extends OperationVariables,
  TSort,
> {
  actionButtons?: (t: QueryResult<TQuery, TVars>) => ButtonProps[];
  dataAccessor: (r: TQuery) => TAccessor | undefined | null;
  columns: ColumnProps<TData, TQuery, TVars>[];
  filters?: PaginationFilter[];
  defaultFilters?: string[];
  defaultSort: TSort;
  buildFilterQuery: (
    allFilters: PaginationFilter[],
    sort: TSort,
    page: number,
    perPage: number,
  ) => TVars | undefined;
  paginationQuery: LazyQueryResultTuple<TQuery, TVars>;
  renderSubComponent?: (
    row: Row<TData>,
    operation?: QueryResult<TQuery, TVars>,
  ) => ReactNode;
  hideButtons?: boolean;
  isDashboard?: boolean;
  enableSearchParams?: boolean;
  noResultsOverride?: NoResultsOverrideType;
}

export function PaginationTable<
  TData,
  TAccessor extends PaginationResponse<TData>,
  TQuery,
  TVars extends OperationVariables,
  TSort,
>({
  actionButtons,
  dataAccessor,
  buildFilterQuery,
  columns,
  defaultFilters,
  defaultSort,
  filters,
  paginationQuery,
  renderSubComponent,
  hideButtons,
  enableSearchParams = true,
  noResultsOverride,
}: PaginationTableProps<TData, TAccessor, TQuery, TVars, TSort>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  // Detect current breakpoint using media queries
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));

  const currentBreakpoint = useMemo(() => {
    if (isXs) return 'xs';
    if (isSm) return 'sm';
    if (isMd) return 'md';
    if (isLg) return 'lg';
    if (isXl) return 'xl';
    return 'xxl';
  }, [isXs, isSm, isMd, isLg, isXl]);

  // Get initial visible columns based on current breakpoint
  const initialVisibleColumns = useMemo(() => {
    return getVisibleColumnsByBreakpoint(columns, currentBreakpoint);
  }, [columns, currentBreakpoint]);

  const [selectedColumns, setSelectedColumns] = useState(initialVisibleColumns);

  // Update selected columns when breakpoint changes
  useEffect(() => {
    const visibleColumns = getVisibleColumnsByBreakpoint(
      columns,
      currentBreakpoint,
    );
    // Only auto-hide columns, don't auto-show them if user manually hid them
    setSelectedColumns((prevSelected) => {
      const newSelected = [...prevSelected];
      // Remove columns that shouldn't be visible at current breakpoint
      return newSelected.filter((col) => visibleColumns.includes(col));
    });
  }, [currentBreakpoint, columns]);

  // Create cleaned columns without minimumBreakpoint for the table component
  const cleanedColumns: ColumnProps<TData, TQuery, TVars>[] = useMemo(() => {
    return map(columns, (column) => omit(column, 'minimumBreakpoint'));
  }, [columns]);

  // Determine what the initial filters are based upon props and the url.
  const initialFilters = useMemo(() => {
    let _defaultFilters = defaultFilters;
    const filterParam = searchParams.get('filter');
    const deepLinkFilters = split(filterParam?.trim(), ',');

    if (enableSearchParams && filterParam && deepLinkFilters.length > 0) {
      _defaultFilters = deepLinkFilters;
    }

    return _defaultFilters;
    // I only want this to run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine what the initial page is based upon value in the url.
  const initialPage = useMemo(() => {
    const pageParam = parseFloat(searchParams.get('page') ?? '');
    if (enableSearchParams && !isNaN(pageParam)) {
      return pageParam;
    }
    return 1;
    // I only want this to run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [page, setPage] = useState<number>(initialPage);
  const [perPage] = useState<number>(15);
  const [pageCount, setPageCount] = useState<number>(1);
  const [itemCount, setItemCount] = useState<number>(10);

  const filteredColumns: ColumnProps<TData, TQuery, TVars>[] = filter(
    cleanedColumns,
    (c) => {
      // Always show button columns
      const isButtonColumn: boolean = !c.label;
      return (c.label && selectedColumns.includes(c.label)) || isButtonColumn;
    },
  );

  const [loadData, operation] = paginationQuery;

  const accessor = useMemo(
    () => operation.data && dataAccessor(operation.data),
    [dataAccessor, operation.data],
  );

  const buildFilters = useCallback(
    (selectedFilters: string[]) => {
      return map(filters, (f) => ({
        ...f,
        isActive: includes(selectedFilters, f.value),
      }));
    },
    [filters],
  );

  const filterOptions = useMemo(() => {
    return map(filters, (f) => ({
      ...f,
      isActive: includes(initialFilters, f.value),
    }));
  }, [filters, initialFilters]);

  const [allFilters, setAllFilters] = useState<PaginationFilter[]>(
    buildFilters(initialFilters || []),
  );

  const handleSetPage = useCallback(
    (newPage: number) => {
      setPage(newPage);
      if (enableSearchParams) {
        searchParams.set('page', newPage.toString());

        setSearchParams(searchParams);
      }
      operation.refetch(
        buildFilterQuery(allFilters, defaultSort, newPage, perPage),
      );
    },
    [
      allFilters,
      buildFilterQuery,
      defaultSort,
      operation,
      perPage,
      searchParams,
      setSearchParams,
      enableSearchParams,
    ],
  );

  useEffect(() => {
    const pageParam = searchParams.get('page');
    const filterParam = searchParams.get('filter');

    if (pageParam) {
      setPage(parseInt(pageParam));
    } else {
      if (enableSearchParams) {
        searchParams.set('page', page.toString());
      }
    }

    if (filters) {
      if (filterParam) {
        setAllFilters(buildFilters(split(filterParam, ',')));
      } else {
        if (enableSearchParams) {
          searchParams.set(
            'filter',
            join(
              map(
                filter(allFilters, (f) => !!f.isActive),
                (m) => m.value,
              ),
              ',',
            ),
          );
        }
      }
    }

    if (enableSearchParams) {
      setSearchParams(searchParams, { replace: true });
    }

    if (!operation.loading && !operation.called && !operation.data) {
      loadData({
        variables: buildFilterQuery(allFilters, defaultSort, page, perPage),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    operation.loading,
    operation.called,
    operation.data,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (operation.data) {
      if (!isNil(accessor?.pageInfo.itemCount)) {
        setItemCount(accessor.pageInfo.itemCount);
      }
      if (!isNil(accessor?.pageInfo.pageCount)) {
        setPageCount(accessor.pageInfo.pageCount);
      }

      // reset page to one if current page is no longer valid
      if (page > (accessor?.pageInfo?.pageCount ?? 0)) {
        setPage(1);
        if (enableSearchParams) {
          searchParams.set('page', '1');
        }
      }
    }
  }, [
    accessor,
    operation,
    operation.data,
    setPage,
    page,
    enableSearchParams,
    searchParams,
  ]);

  const renderTable = () => {
    if (!operation.loading && !accessor?.items?.length) {
      return <NoResultsText textOverride={noResultsOverride} />;
    }

    return (
      <Table<TData, TQuery, TVars>
        rows={accessor?.items ?? []}
        rowAlignment="middle"
        renderSubComponent={renderSubComponent}
        loading={operation.loading || !operation.called}
        columns={map(filteredColumns, (c) => {
          if (c.formatter) {
            c.formatter = bind(
              c.formatter,
              c,
              bind.placeholder,
              bind.placeholder,
              operation,
            );
          }
          return c;
        })}
      />
    );
  };

  return (
    <Box sx={{ pb: 1 }}>
      {!hideButtons && (
        <Box display="flex" justifyContent="flex-end" gap={2} px={1}>
          {map(
            concat(actionButtons ? actionButtons(operation) : [], {
              icon: faRefresh,
              isLoading: operation.networkStatus === NetworkStatus.refetch,
              onClick: () => operation.refetch(),
            }),
            (b, idx) => (
              <Button
                size="sm"
                key={idx}
                content={isMd ? b.content : ''}
                {...omit(b, 'content')}
              />
            ),
          )}
          {/* Filters */}
          {filters && (
            <DropdownMultiSelect
              size="sm"
              title="Filters"
              options={filterOptions}
              resetSelections
              content="Apply"
              icon={faFilter}
              onApply={(options) => {
                const activeFilters = map(
                  filter(options, (f) => !!f.isActive),
                  (m) => m.value,
                );

                if (enableSearchParams) {
                  searchParams.set('filter', join(activeFilters, ','));
                  setSearchParams(searchParams, { replace: true });
                }

                return operation.refetch(
                  buildFilterQuery(
                    buildFilters(activeFilters),
                    defaultSort,
                    page,
                    perPage,
                  ),
                );
              }}
            />
          )}
          {/* Column Filters */}
          <DropdownMultiSelect
            title="Columns"
            size="sm"
            options={reduce(
              columns,
              (res: Option[], col, key) => {
                if (col.label) {
                  const isAvailableAtCurrentBreakpoint =
                    !col.minimumBreakpoint ||
                    BREAKPOINT_ORDER.indexOf(currentBreakpoint as any) >=
                      BREAKPOINT_ORDER.indexOf(col.minimumBreakpoint);

                  res.push({
                    label: col.label,
                    value: col.label || '',
                    isActive:
                      selectedColumns.includes(col.label) &&
                      isAvailableAtCurrentBreakpoint,
                  });
                }
                return res;
              },
              [],
            )}
            content="Apply"
            resetSelections
            icon={faColumns}
            onApply={(options) => {
              const selectedOptions = map(
                filter(options, (f) => !!f.isActive),
                (m) => m.value.replace(' (hidden on small screens)', ''), // Clean the label
              );
              setSelectedColumns(selectedOptions);
            }}
          />
        </Box>
      )}
      {operation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <pre>
            <code>{JSON.stringify(operation.error, null, 2)}</code>
          </pre>
        </Alert>
      )}
      {renderTable()}
      <Pagination
        setPage={handleSetPage}
        currentPage={page}
        pageCount={pageCount}
        itemCount={itemCount}
      />
    </Box>
  );
}
